import React, { useEffect, useState } from 'react';

import { Alert, Button, Center, Group, SegmentedControl, Stack, Text, useMantineTheme } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconExclamationCircle } from '@tabler/icons-react';
import { useAuth } from 'context/auth-context';
import useAthletePackages from 'hooks/athletePackage/useAthletePackages';
import { useAthleteTracking } from 'hooks/mixpanel/useAthleteTracking';
import useAthleteSalesPortalPackages from 'hooks/salesPortal/useAthleteSalesPortalPackages';
import SimpleHeader from 'layouts/MobileLayout/components/SimpleHeader';
import { useNavigate } from 'react-router-dom';
import { AthletePackageList } from 'types';

import AthletePackageCard from './components/AthletePackageCard';
import Credits from './components/CreditsTab/Credits';

const Packages = (): JSX.Element => {
  const theme = useMantineTheme();
  const isLargerThanXS = useMediaQuery(`(min-width: ${theme.breakpoints.xs}`, false);
  const navigate = useNavigate();

  const { session } = useAuth();
  const athleteTracking = useAthleteTracking();
  const [statusFilter, setStatusFilter] = useState<string>('current');
  const [athletePackages, setAthletePackages] = useState<Array<AthletePackageList>>([]);

  const { data: packages, isSuccess: packagesSuccess } = useAthleteSalesPortalPackages(
    session?.gym?.gymId,
    undefined,
    session?.profile?.profileId,
  );

  const hasSalesPortalPackages = packagesSuccess && packages?.data?.length > 0;

  const { data, isLoading, isSuccess, isError, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useAthletePackages({
      athlete: session?.athlete?.athleteId,
      statuses: statusFilter === 'current' ? ['ACTIVE', 'UPCOMING', 'HOLD'] : ['CANCELLED', 'ENDED'],
      enabled: statusFilter !== 'credits',
    });

  useEffect(() => {
    if (isSuccess && data) {
      setAthletePackages(data.pages.map(page => page.data.results).flat());
    }
  }, [data, isSuccess]);

  const handleTabChange = (oldValue: string, newValue: string): void => {
    setStatusFilter(newValue);
    athleteTracking.trackPackagesTabSelected(newValue, oldValue);
  };

  return (
    <>
      <SimpleHeader title='Packages' />

      <Stack className='content'>
        <Group noWrap={!isLargerThanXS} position={hasSalesPortalPackages ? 'apart' : 'center'}>
          <SegmentedControl
            size='md'
            value={statusFilter}
            onChange={(value: string) => handleTabChange(statusFilter, value)}
            data={[
              { label: 'Current', value: 'current' },
              { label: 'History', value: 'history' },
              { label: 'Credits', value: 'credits' },
            ]}
            sx={{ flexGrow: hasSalesPortalPackages ? 1 : 0 }}
          />
          {hasSalesPortalPackages && (
            <Button onClick={() => navigate('/packages/sales-portal')}>
              {isLargerThanXS ? 'Explore Packages' : 'Purchase'}
            </Button>
          )}
        </Group>

        {!isLoading && athletePackages.length === 0 && statusFilter !== 'credits' && (
          <Center my='auto'>
            <Text color='dimmed'>No packages found.</Text>
          </Center>
        )}

        {isError && (
          <Center sx={{ flexGrow: 1 }} p='lg'>
            <Alert icon={<IconExclamationCircle size={theme.fontSizes.md} />} title='Error' color='red'>
              Something went wrong while fetching data!
            </Alert>
          </Center>
        )}

        {(statusFilter === 'current' || statusFilter === 'history') && (
          <>
            {athletePackages?.map(athletePackage => (
              <AthletePackageCard key={athletePackage.athletePackageId} athletePackage={athletePackage} />
            ))}
            {hasNextPage && (
              <Group grow>
                <Button
                  size='md'
                  variant='light'
                  onClick={() => fetchNextPage()}
                  loading={isFetchingNextPage}
                >
                  Show more
                </Button>
              </Group>
            )}
          </>
        )}
        {statusFilter === 'credits' && <Credits athleteId={session?.athlete?.athleteId} />}
      </Stack>
    </>
  );
};

export default Packages;
